import * as types from '../mutation-types';
import { createActionHelpers } from 'vuex-loading';
import Event from '@js/franchise_component/franchise-inventory/stock-receive/stock-receive-event';
import EventStock from '@js/franchise_component/franchise-inventory/stock/stock-event';
import Auth from '@js/plugins/auth';

const { startLoading, endLoading } = createActionHelpers({
    moduleName: 'loading',
});

const state = {
    stock_movement: {
        outlet_id: null,
        reference_id: null,
        name: null,
        status: null,
        outlet_name: null,
        settled_at: null,
        settled_by: null,
        settled_name: null,
        created_by: null,
        created_name: null,
        supplier: {
            name: null,
            stocks: [],
        },
        stock_movement_info: {
            service_mode: null,
        },
    },
    stock_movement_products: [],
};

const getters = {
    stockMovement() {
        return state.stock_movement;
    },

    hasFinalized: state => state.stock_movement.status == 2,

    serviceMode: state =>
        state.stock_movement.stock_movement_info.service_mode == 'shift',

    completionStatus(state) {
        if (state.stock_movement.status == 2) {
            return 'Completed';
        }

        return 'In Progress';
    },

    stockMovementProducts: state =>
        state.stock_movement_products.map((p, i) => {
            p.row_index = i + 1;
            return p;
        }),

    findStockIndex: (state, getters) => stock_id => {
        return getters.stockMovementProducts.findIndex(
            s => s.stock_id == stock_id,
        );
    },

    supplier: state => state.stock_movement.supplier,

    grandTotalCost: (state, getters) => {
        return getters.stockMovementProducts
            .map(product => {
                return product.supplier_price * product.total_quantity;
            })
            .reduce((a, c) => a + c, 0);
    },

    totalCurrentQty: (state, getters) => {
        return getters.stockMovementProducts
            .map(product => {
                return parseFloat(product.quantity_before);
            })
            .reduce((a, b) => a + b, 0);
    },

    totalUnitCost: (state, getters) => {
        return getters.stockMovementProducts
            .map(product => {
                const cost = parseFloat(
                    parseFloat(product.supplier_price)
                        .toFixed(7)
                        .slice(0, -3),
                );

                const validCost = !_.isNaN(cost);
                return validCost ? cost : 0;
            })
            .reduce((a, c) => a + c, 0);
    },

    totalReceiveQty: (state, getters) => {
        return getters.stockMovementProducts
            .map(product => {
                const validQty =
                    !_.isNaN(product.total_quantity) &&
                    !_.isNull(product.total_quantity) &&
                    !_.isEmpty(product.total_quantity);

                return validQty ? parseFloat(product.total_quantity) : 0;
            })
            .reduce((a, b) => a + b, 0);
    },

    productCount: (state, getters) => {
        if (getters.stockMovementProducts.length !== 0) {
            return `Total: ${getters.stockMovementProducts.length}`;
        }

        return '';
    },
};

const mutations = {
    [types.SET_STOCK_RECEIVE](state, data) {
        state.stock_movement = {
            ...data,
            stock_movement_info:
                data.stock_movement_info ||
                state.stock_movement.stock_movement_info,
        };
    },

    [types.SET_STOCK_RECEIVE_ATTR](state, { key, value }) {
        state.stock_movement[key] = value;
    },

    setStockMovementProducts(state, data) {
        state.stock_movement_products = data;
    },

    [types.ADD_RECEIVE_STOCK](state, stockReceive) {
        state.stock_movement_products.push(stockReceive);
    },

    [types.UPDATE_RECEIVE_STOCK](state, { index, key, value }) {
        state.stock_movement_products[index][key] = value;
    },

    [types.REMOVE_STOCK_MOVEMENT_PRODUCT](state, index) {
        state.stock_movement_products.splice(index, 1);
    },

    merge(state, data) {
        state.stock_movement.stock_movement_products = data;
    },

    setCommitBy(state) {
        state.stock_movement.settled_by = Auth.user().id;
        state.stock_movement.settled_name = Auth.user().name;
    },
};

const actions = {
    setStockReceiveAttr({ commit }, { key, value }) {
        commit(types.SET_STOCK_RECEIVE_ATTR, { key, value });
    },

    addReceiveStock({ commit, getters }, { stock }) {
        let supplier_price = stock.supplier_cost;

        const stock_movement_product = {
            name: stock.name,
            sku: stock.sku,
            code: stock.code,
            stock_id: stock.id,
            stock: stock,
            quantity_before: stock.quantity,
            total_quantity: null,
            supplier_price,
            total_cost: null,
            rsp: null,
        };

        commit(types.ADD_RECEIVE_STOCK, stock_movement_product);
    },

    updateReceiveStock({ commit, getters }, { stock_id, key, value }) {
        const index = getters.findStockIndex(stock_id);
        commit(types.UPDATE_RECEIVE_STOCK, { index, key, value });
    },

    removeStockMovementProduct({ commit, getters }, stock_id) {
        const stockIndex = getters.findStockIndex(stock_id);

        if (stockIndex >= 0) {
            commit(types.REMOVE_STOCK_MOVEMENT_PRODUCT, stockIndex);
            EventStock.$emit('remove-stock-id', stock_id);
        }
    },

    async showStockReceive({ commit, dispatch }, id) {
        const url = Laroute.route('inventory.stock-receive.show', { id });
        startLoading(dispatch, 'fetch-stock-receive');

        try {
            const { data } = await axios.get(url);
            endLoading(dispatch, 'fetch-stock-receive');
            commit(types.SET_STOCK_RECEIVE, data);

            return data;
        } catch (e) {
            endLoading(dispatch, 'fetch-stock-receive');
            throw e;
        }
    },

    async createStockReceive({ commit, dispatch }, params) {
        const url = Laroute.route('inventory.stock-receive.create');
        startLoading(dispatch, 'save-stock-receive');

        try {
            const { data } = await axios.post(url, params);
            endLoading(dispatch, 'save-stock-receive');

            return data;
        } catch (e) {
            endLoading(dispatch, 'save-stock-receive');
            throw e;
        }
    },

    async getProductList({ commit, dispatch }, id) {
        const url = Laroute.route('inventory.stock-receive.product-list', {
            id,
        });

        startLoading(dispatch, 'fetch-product-list');

        try {
            const { data } = await axios.get(url);
            endLoading(dispatch, 'fetch-product-list');
            commit('setStockMovementProducts', data);

            return data;
        } catch (e) {
            endLoading(dispatch, 'fetch-product-list');
            throw e;
        }
    },

    async updateStockMovement({ commit, dispatch, state }) {
        const url = Laroute.route('inventory.stock-receive.update');
        startLoading(dispatch, 'save-stock-receive');
        commit('merge', state.stock_movement_products);

        try {
            const { data } = await axios.put(url, state.stock_movement);
            endLoading(dispatch, 'save-stock-receive');
            commit(types.SET_STOCK_RECEIVE, data.stock_movement);
            commit('setStockMovementProducts', data.stock_movement_products);
            EventStock.$emit('stock-list-reload');

            return data;
        } catch (e) {
            endLoading(dispatch, 'save-stock-receive');
            throw e;
        }
    },

    async received({ commit, dispatch, state }) {
        const url = Laroute.route('inventory.stock-receive.received');
        startLoading(dispatch, 'mark-received');
        commit('merge', state.stock_movement_products);
        commit('setCommitBy');

        try {
            const { data } = await axios.post(url, state.stock_movement);
            endLoading(dispatch, 'mark-received');
            commit(types.SET_STOCK_RECEIVE, data.stock_movement);
            commit('setStockMovementProducts', data.stock_movement_products);

            return data;
        } catch (e) {
            endLoading(dispatch, 'mark-received');
            throw e;
        }
    },

    async exportPdf({ commit, dispatch }, params) {
        const url = Laroute.route('inventory.stock-receive.exportAsFile');

        try {
            const { data } = await axios.post(url, { params });
        } catch (e) {
            throw e;
        }
    },

    async getRsp(
        { commit, dispatch },
        { stock_id, outlet_id, code, unique_id },
    ) {
        const url = Laroute.route('inventory.stock.price-checker', {
            outlet_id: outlet_id,
            code: code,
            unique_id: unique_id,
        });
        startLoading(dispatch, 'fetch-price-checker');

        try {
            const { data } = await axios.get(url);
            endLoading(dispatch, 'fetch-price-checker');

            const station_unit_price = data.station_unit_price;

            dispatch('updateReceiveStock', {
                stock_id: stock_id,
                key: 'rsp',
                value: station_unit_price,
            });

            return data;
        } catch (e) {
            endLoading(dispatch, 'fetch-price-checker');
            throw e;
        }
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};



// WEBPACK FOOTER //
// ./resources/assets/js/store/modules/inventory-stock-receive.js